/**
 * ActionEnablementEditor - Checkbox-based action enablement for copilot config
 * 
 * Part of the AI Actions System Refactor.
 * Replaces legacy actions[] array display with checkboxes from ACTION_REGISTRY.
 */

import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Edit, Plus, Trash2, GitMerge, Scissors, Link, Unlink, 
  RefreshCw, Layers, Link2
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Action definitions (mirrors backend action_registry.py)
const ACTION_REGISTRY = {
  AWAIT_FEEDBACK: {
    name: "Await Feedback",
    description: "No action needed - just providing information or analysis",
    icon: RefreshCw,
    tier: 1,
    requiresConfirmation: false,
    supportedEntities: ["all"],
  },
  UPDATE_ITEM: {
    name: "Update Item",
    description: "Update fields on a single entity",
    icon: Edit,
    tier: 1,
    requiresConfirmation: false,
    supportedEntities: ["feature", "epic", "goal", "initiative", "persona", "product", "request"],
  },
  CREATE_ITEM: {
    name: "Create Item",
    description: "Create a new entity",
    icon: Plus,
    tier: 1,
    requiresConfirmation: false,
    supportedEntities: ["feature", "epic", "goal", "initiative", "persona"],
    scopeRestriction: "collection",
  },
  LINK_ITEMS: {
    name: "Link Items",
    description: "Create a relationship between two entities",
    icon: Link,
    tier: 1,
    requiresConfirmation: false,
    supportedEntities: ["feature", "epic", "goal", "initiative"],
  },
  UNLINK_ITEMS: {
    name: "Unlink Items",
    description: "Remove a relationship between two entities",
    icon: Unlink,
    tier: 1,
    requiresConfirmation: false,
    supportedEntities: ["feature", "epic", "goal", "initiative"],
  },
  DELETE_ITEM: {
    name: "Delete Item",
    description: "Permanently remove an entity",
    icon: Trash2,
    tier: 2,
    requiresConfirmation: true,
    supportedEntities: ["feature", "epic", "goal", "initiative", "persona"],
  },
  MERGE_ITEMS: {
    name: "Merge Items",
    description: "Combine multiple items into a single new item",
    icon: GitMerge,
    tier: 2,
    requiresConfirmation: true,
    supportedEntities: ["feature", "epic"],
    scopeRestriction: "collection",
  },
  SPLIT_ITEM: {
    name: "Split Item",
    description: "Split one item into multiple items",
    icon: Scissors,
    tier: 2,
    requiresConfirmation: true,
    supportedEntities: ["feature"],
    scopeRestriction: "item",
  },
  BULK_UPDATE: {
    name: "Bulk Update",
    description: "Update multiple items at once",
    icon: Layers,
    tier: 2,
    requiresConfirmation: true,
    supportedEntities: ["feature", "epic", "goal", "initiative", "persona"],
    scopeRestriction: "collection",
  },
  BULK_CREATE: {
    name: "Bulk Create",
    description: "Create multiple items at once",
    icon: Plus,
    tier: 2,
    requiresConfirmation: true,
    supportedEntities: ["feature", "epic", "goal", "initiative", "persona"],
    scopeRestriction: "collection",
  },
  BULK_LINK: {
    name: "Bulk Link",
    description: "Link multiple items to a target",
    icon: Link2,
    tier: 2,
    requiresConfirmation: true,
    supportedEntities: ["feature", "epic", "goal", "initiative"],
    scopeRestriction: "collection",
  },
};

// Tier labels and colors
const TIER_CONFIG = {
  1: { label: "Direct Apply", color: "text-green-600", bgColor: "bg-green-500/10" },
  2: { label: "Confirmation Required", color: "text-amber-600", bgColor: "bg-amber-500/10" },
};

/**
 * Detect entity type from agent_type string
 */
const detectEntityType = (agentType) => {
  if (!agentType) return 'feature';
  const lower = agentType.toLowerCase();
  
  if (lower.includes('feature')) return 'feature';
  if (lower.includes('epic')) return 'epic';
  if (lower.includes('goal')) return 'goal';
  if (lower.includes('initiative')) return 'initiative';
  if (lower.includes('persona')) return 'persona';
  if (lower.includes('strategy') || lower.includes('product')) return 'product';
  if (lower.includes('request')) return 'request';
  
  return 'feature';
};

/**
 * Detect scope from agent_type string
 */
const detectScope = (agentType) => {
  if (!agentType) return 'collection';
  const lower = agentType.toLowerCase();
  
  // Singular names typically indicate item scope
  if (lower.endsWith('_copilot') && !lower.endsWith('s_copilot')) {
    return 'item';
  }
  
  return 'collection';
};

export const ActionEnablementEditor = ({ 
  agentType,
  entityType: explicitEntityType,
  scope: explicitScope,
  enabledActions = [],
  legacyActions = [],
  onChange,
  colors = {},
  className
}) => {
  // Detect entity type and scope
  const entityType = explicitEntityType || detectEntityType(agentType);
  const scope = explicitScope || detectScope(agentType);
  
  // Filter actions available for this entity type and scope
  const availableActions = Object.entries(ACTION_REGISTRY)
    .filter(([_, action]) => {
      // Check entity support
      const supported = action.supportedEntities;
      if (!supported.includes('all') && !supported.includes(entityType)) {
        return false;
      }
      
      // Check scope restriction
      if (action.scopeRestriction && action.scopeRestriction !== scope) {
        return false;
      }
      
      return true;
    })
    .map(([id, action]) => ({ id, ...action }));
  
  // Initialize from legacy actions if no enabled_actions set
  const getActionEnabled = (actionId) => {
    // AWAIT_FEEDBACK is always enabled (core action)
    if (actionId === 'AWAIT_FEEDBACK') {
      return true;
    }
    
    // If enabled_actions exists and has items, use it
    if (enabledActions && enabledActions.length > 0) {
      return enabledActions.includes(actionId);
    }
    
    // Fall back to legacy actions array
    if (legacyActions && legacyActions.length > 0) {
      // Map legacy action_id to new action types
      const legacyMapping = {
        'create_feature': 'CREATE_ITEM',
        'update_feature': 'UPDATE_ITEM',
        'merge_features': 'MERGE_ITEMS',
        'delete_feature': 'DELETE_ITEM',
        'split_feature': 'SPLIT_ITEM',
        'link_persona': 'LINK_ITEMS',
        'bulk_update': 'BULK_UPDATE',
        'bulk_link_personas': 'BULK_LINK',
        'create_epic': 'CREATE_ITEM',
        'update_epic': 'UPDATE_ITEM',
        'merge_epics': 'MERGE_ITEMS',
      };
      
      return legacyActions.some(a => {
        if (!a.is_enabled) return false;
        const mapped = legacyMapping[a.action_id];
        return mapped === actionId;
      });
    }
    
    // Default: enable core actions
    return ['UPDATE_ITEM', 'CREATE_ITEM'].includes(actionId);
  };
  
  const handleToggle = (actionId, enabled) => {
    let newEnabled;
    if (enabled) {
      newEnabled = [...new Set([...enabledActions, actionId])];
    } else {
      newEnabled = enabledActions.filter(id => id !== actionId);
    }
    onChange(newEnabled);
  };
  
  const handleEnableAll = () => {
    onChange(availableActions.map(a => a.id));
  };
  
  const handleDisableAll = () => {
    // Keep AWAIT_FEEDBACK always enabled
    onChange(['AWAIT_FEEDBACK']);
  };
  
  // Group by tier
  const tier1Actions = availableActions.filter(a => a.tier === 1);
  const tier2Actions = availableActions.filter(a => a.tier === 2);
  
  const enabledCount = availableActions.filter(a => getActionEnabled(a.id)).length;
  
  const renderActionGroup = (actions, tierConfig) => (
    <div className="space-y-2">
      <div className={cn("text-xs font-medium px-2 py-1 rounded", tierConfig.bgColor, tierConfig.color)}>
        {tierConfig.label}
      </div>
      <div className="grid gap-2">
        {actions.map((action) => {
          const enabled = getActionEnabled(action.id);
          const Icon = action.icon;
          return (
            <div 
              key={action.id}
              className={cn(
                "flex items-center gap-3 p-3 rounded-lg transition-colors border",
                enabled 
                  ? "bg-blue-500/5 border-blue-500/20" 
                  : "bg-gray-500/5 border-transparent"
              )}
            >
              <Checkbox
                id={`action-${action.id}`}
                checked={enabled}
                onCheckedChange={(checked) => handleToggle(action.id, checked)}
                disabled={action.id === 'AWAIT_FEEDBACK'} // Always keep AWAIT_FEEDBACK
              />
              <div className={cn(
                "p-1.5 rounded",
                enabled ? "bg-blue-500/20" : "bg-gray-500/10"
              )}>
                <Icon className={cn("w-4 h-4", enabled ? "text-blue-500" : colors.textMuted)} />
              </div>
              <div className="flex-1 min-w-0">
                <Label 
                  htmlFor={`action-${action.id}`}
                  className={cn(
                    "text-sm font-medium cursor-pointer block",
                    enabled ? colors.textPrimary : colors.textMuted
                  )}
                >
                  {action.name}
                </Label>
                <p className={cn("text-xs truncate", colors.textMuted)}>
                  {action.description}
                </p>
              </div>
              {action.requiresConfirmation && (
                <Badge variant="outline" className="text-xs shrink-0">
                  Confirm
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  
  return (
    <Card className={cn("p-4", colors.bgCard, colors.borderPrimary, className)}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className={cn("font-medium", colors.textPrimary)}>
            Enabled Actions
          </h4>
          <p className={cn("text-xs mt-0.5", colors.textMuted)}>
            Actions this copilot can suggest ({scope} scope)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {enabledCount} / {availableActions.length}
          </Badge>
          <button 
            onClick={handleEnableAll}
            className={cn("text-xs underline", colors.textMuted, "hover:text-blue-500")}
          >
            All
          </button>
          <button 
            onClick={handleDisableAll}
            className={cn("text-xs underline", colors.textMuted, "hover:text-blue-500")}
          >
            Minimal
          </button>
        </div>
      </div>
      
      <div className="space-y-4">
        {tier1Actions.length > 0 && renderActionGroup(tier1Actions, TIER_CONFIG[1])}
        {tier2Actions.length > 0 && renderActionGroup(tier2Actions, TIER_CONFIG[2])}
      </div>
    </Card>
  );
};

export default ActionEnablementEditor;
